import {
  currentAuthStorage,
  currentAuthStorageKey,
} from "@/api/supabaseClient";
import { removePersistedAuthSession } from "@/auth/authStorageKey";

/**
 * Web-Logout. Supabase rotiert den Refresh-Token bei jedem Refresh, deshalb
 * gilt während des Latches jede Session derselben User-ID als die alte,
 * unabhängig von den Tokens. TOKEN_REFRESHED und INITIAL_SESSION sind in
 * diesem Fenster nie ein Login. Durch geht nur ein explizites SIGNED_IN
 * (allowNextSignedIn) oder eine andere User-ID.
 * Nach dem Latch sperren nur noch die Tokens, die der Logout gesehen hat.
 * Ein explizites SIGNED_IN (allowNextSignedIn) oder PASSWORD_RECOVERY,
 * auch als Broadcast aus einem anderen Tab, setzt die User-ID und das
 * Flag zurück. Gesehene Tokens bleiben gesperrt: der Refresh-Token ist
 * serverseitig weiter gültig, wenn der Server-Logout fehlschlägt oder
 * blockiert wird, lokal wird er verworfen. Eine neue Sitzung mit anderen
 * Tokens bleibt eingeloggt.
 */
export const WEB_SIGNOUT_DEADLINE_MS = 4_000;
export const WEB_SIGNOUT_LATCH_BUFFER_MS = 3_000;
export const WEB_SIGNOUT_LATCH_MAX_MS = 10_000;

export type SignedOutIdentity = {
  userId: string | null;
  accessToken: string | null;
  refreshToken: string | null;
};

type SessionLike = {
  access_token?: string | null;
  refresh_token?: string | null;
  user?: { id?: string | null } | null;
} | null;

type Latch = {
  userId: string | null;
  startedAt: number;
  until: number;
};

let latch: Latch | null = null;
let signedOutUserId: string | null = null;
let explicitSignIn = false;
/** Access- und Refresh-Tokens, die dieser Logout konkret gesehen hat. */
const blockedAuthTokens = new Set<string>();

export function beginWebSignOut(identity: SignedOutIdentity, now = Date.now()): void {
  signedOutUserId = identity.userId;
  explicitSignIn = false;
  blockAuthToken(identity.accessToken);
  blockAuthToken(identity.refreshToken);
  latch = {
    userId: identity.userId,
    startedAt: now,
    until: now + WEB_SIGNOUT_LATCH_MAX_MS,
  };
}

/** Logout ist fertig oder die Frist ist erreicht: noch kurz puffern, höchstens 10 s ab Start. */
export function completeWebSignOut(now = Date.now()): void {
  if (!latch) return;
  const buffered = now + WEB_SIGNOUT_LATCH_BUFFER_MS;
  const cap = latch.startedAt + WEB_SIGNOUT_LATCH_MAX_MS;
  latch.until = Math.min(buffered, cap);
}

export function webSignOutLatchActive(now = Date.now()): boolean {
  if (!latch) return false;
  if (now >= latch.until) {
    latch = null;
    return false;
  }
  return true;
}

/** Nächster SIGNED_IN kommt aus dem Login-Formular, nicht aus einem Refresh. */
export function allowNextSignedIn(): void {
  explicitSignIn = true;
}

export function cancelExplicitSignIn(): void {
  explicitSignIn = false;
}

export function resetWebSignOutForTests(): void {
  latch = null;
  signedOutUserId = null;
  explicitSignIn = false;
  blockedAuthTokens.clear();
}

function blockAuthToken(token: string | null | undefined): void {
  if (token) blockedAuthTokens.add(token);
}

/** User-ID und allowNextSignedIn zurücksetzen. Gesehene Tokens bleiben gesperrt. */
function endSignedOutIdentity(): void {
  signedOutUserId = null;
  explicitSignIn = false;
}

function sameUser(session: SessionLike): boolean {
  const userId = session?.user?.id ?? null;
  return signedOutUserId != null && userId === signedOutUserId;
}

function differentUser(session: SessionLike): boolean {
  const userId = session?.user?.id ?? null;
  return signedOutUserId != null && userId != null && userId !== signedOutUserId;
}

function hasBlockedAuthToken(session: SessionLike): boolean {
  const access = session?.access_token ?? null;
  const refresh = session?.refresh_token ?? null;
  return (
    (access != null && blockedAuthTokens.has(access)) ||
    (refresh != null && blockedAuthTokens.has(refresh))
  );
}

function blockSeenSessionTokens(session: SessionLike): void {
  if (!sameUser(session)) return;
  blockAuthToken(session?.access_token);
  blockAuthToken(session?.refresh_token);
}

function isExplicitLogin(event: string | null): boolean {
  return (event === "SIGNED_IN" && explicitSignIn) || event === "PASSWORD_RECOVERY";
}

/**
 * true, wenn diese Session nicht in den React-State darf.
 * `event` null steht für getSession (Reload), nicht für TOKEN_REFRESHED.
 */
export function shouldDiscardAuthEvent(
  event: string | null,
  session: SessionLike,
  now = Date.now(),
): boolean {
  if (!session) return false;
  if (isExplicitLogin(event)) {
    endSignedOutIdentity();
    // Gesehene Tokens bleiben gesperrt, auch wenn das Ereignis ein Login ist.
    if (hasBlockedAuthToken(session)) return true;
    return false;
  }
  if (signedOutUserId == null && blockedAuthTokens.size === 0) return false;
  const latched = webSignOutLatchActive(now);
  if (latched && (event === "TOKEN_REFRESHED" || event === "INITIAL_SESSION")) {
    blockSeenSessionTokens(session);
    return true;
  }
  if (differentUser(session) && !hasBlockedAuthToken(session)) return false;
  if (hasBlockedAuthToken(session)) return true;
  if (latched && sameUser(session)) {
    blockSeenSessionTokens(session);
    return true;
  }
  return false;
}

export function shouldDiscardSignedOutSession(session: SessionLike, now = Date.now()): boolean {
  return shouldDiscardAuthEvent(null, session, now);
}

function sessionFromStorage(sessionRaw: string | null, userRaw: string | null): SessionLike {
  if (!sessionRaw) return null;
  try {
    const parsed = JSON.parse(sessionRaw) as {
      access_token?: string;
      refresh_token?: string;
      user?: { id?: string };
    };
    let userId = parsed.user?.id ?? null;
    if (!userId && userRaw) {
      try {
        const userParsed = JSON.parse(userRaw) as { id?: string; user?: { id?: string } };
        userId = userParsed.user?.id ?? userParsed.id ?? null;
      } catch {
        userId = null;
      }
    }
    return {
      access_token: parsed.access_token ?? null,
      refresh_token: parsed.refresh_token ?? null,
      user: { id: userId },
    };
  } catch {
    return null;
  }
}

export function persistedSessionIsSignedOut(
  sessionRaw: string | null,
  userRaw: string | null,
  now = Date.now(),
): boolean {
  const session = sessionFromStorage(sessionRaw, userRaw);
  if (!session) return false;
  if (hasBlockedAuthToken(session)) return true;
  return webSignOutLatchActive(now) && sameUser(session);
}

async function readStorageItem(
  storage: { getItem?: (key: string) => unknown },
  key: string,
): Promise<string | null> {
  if (typeof storage.getItem !== "function") return null;
  const value = await storage.getItem(key);
  return typeof value === "string" ? value : null;
}

/**
 * Entfernt die Keys nur, wenn der Storage die alte User-ID noch im Latch
 * hält oder ein genau gesehenes Token des Logouts. Eine neue Sitzung
 * (andere Tokens, nach dem Latch auch dieselbe User-ID) bleibt stehen.
 */
export async function discardWebAuthStorage(now = Date.now()): Promise<void> {
  if (signedOutUserId == null && blockedAuthTokens.size === 0) return;
  try {
    const storage = currentAuthStorage();
    const storageKey = currentAuthStorageKey();
    if (!storageKey) return;
    const sessionRaw = await readStorageItem(storage, storageKey);
    const userRaw = await readStorageItem(storage, `${storageKey}-user`);
    if (!persistedSessionIsSignedOut(sessionRaw, userRaw, now)) return;
    await removePersistedAuthSession(storage, storageKey);
  } catch (err: unknown) {
    console.error(
      "AuthContext: local session remove failed",
      err instanceof Error ? err.message : err,
    );
  }
}
